/**
 * Constantes métier de la brique logistique, SANS aucune dépendance runtime (ni vine, ni Lucid) :
 * ce module est exporté par le barrel (`index.ts`), donc embarqué par Metro dans le bundle Expo.
 * Toute règle de validation (qui, elle, importe vine) reste dans `validation/logistics.ts`, en
 * deep-import uniquement.
 *
 * Point de vérité unique des énumérations du module : les modèles Lucid, les schémas Vine, le
 * contrat du solveur et le back-office s'y réfèrent plutôt que de recopier les littéraux.
 */

/** `logistics_vehicles.vehicle_type`. */
export const LOGISTICS_VEHICLE_TYPES = ['VL', 'PL', 'SL'] as const
export type LogisticsVehicleType = (typeof LOGISTICS_VEHICLE_TYPES)[number]

/**
 * `store_logistics_profiles.allowed_vehicle_types` : sous-ensemble strict des types de véhicule.
 * `SL` (semi-remorque) n'en fait jamais partie — aucune boutique ne se livre en semi.
 */
export const LOGISTICS_STORE_VEHICLE_TYPES = ['VL', 'PL'] as const
export type LogisticsStoreVehicleType = (typeof LOGISTICS_STORE_VEHICLE_TYPES)[number]

/** `logistics_driver_licences.category` : permis B/C/CE. */
export const LOGISTICS_LICENCE_CATEGORIES = ['B', 'C', 'CE'] as const
export type LogisticsLicenceCategory = (typeof LOGISTICS_LICENCE_CATEGORIES)[number]

/** `logistics_driver_profiles.employment_type`. */
export const LOGISTICS_EMPLOYMENT_TYPES = ['internal', 'contractor'] as const
export type LogisticsEmploymentType = (typeof LOGISTICS_EMPLOYMENT_TYPES)[number]

/** `store_logistics_profiles.delivery_mode`. */
export const LOGISTICS_DELIVERY_MODES = ['autonomous', 'assisted'] as const
export type LogisticsDeliveryMode = (typeof LOGISTICS_DELIVERY_MODES)[number]

/** Rattachement à exactement un atelier OU un hub (CHECK `*_single_home` côté base). */
export const hasExactlyOneHome = (home: {
  homeCentralKitchenId: number | null
  homeHubId: number | null
}): boolean => (home.homeCentralKitchenId !== null) !== (home.homeHubId !== null)

// --- Paramètres de simulation -----------------------------------------------------------

/**
 * Paramètres de simulation logistique.
 *
 * Deux familles : ceux qui ont une valeur par défaut défendable (`LOGISTICS_DEFAULT_PARAMETERS`)
 * et ceux qui n'en ont aucune (`LOGISTICS_REQUIRED_PARAMETERS`) — les seconds ne s'inventent pas,
 * une simulation lancée sans eux conclut `input_incomplete` plutôt que de chiffrer au hasard.
 */
export const LOGISTICS_DEFAULT_PARAMETERS = {
  /** Toutes les boutiques livrées avant cette heure locale. */
  deliveryDeadline: '10:30',
  /** Durée d'un arrêt boutique : 20 min. */
  stopDurationSeconds: 1200,
  /** Rupture de charge sur un hub : 45 min. À confirmer par Saad. */
  hubTransferDurationSeconds: 2700,
  /**
   * Conduite continue maximale avant pause obligatoire : 4 h 30. Confirmé par Saad le 21/09/2026,
   * qui corrigeait la maquette — « c'est quatre heures et demie avec une pause de 45 minutes,
   * encore 4 h 30 avec une pause de 45 minutes », pour 9 h de conduite au total.
   */
  maxContinuousDrivingMinutes: 270,
  /** Pause obligatoire après la conduite continue maximale : 45 min. Confirmé le 21/09/2026. */
  mandatoryBreakMinutes: 45,
  /** Vol d'oiseau → route, adaptateur de dev uniquement (quand aucune matrice mesurée). */
  detourFactor: 1.3,
} as const

export type LogisticsDefaultedParameter = keyof typeof LOGISTICS_DEFAULT_PARAMETERS

/**
 * Sans défaut : à confirmer par Saad.
 *
 * L'unité de charge est le SUPPORT, palette 800×1200 ou pali 800×600 — décision du 21/09/2026 :
 * « on a charge utile, palettes, palis, c'est ça qui décidera des commandes de demain », et « pour
 * l'instant on laisse palettes et palis, mais avec le temps on ne raisonnera qu'en palis ». La
 * cagette a disparu du vocabulaire métier, elle disparaît d'ici.
 *
 * Saad n'a donné ni le rapport palette → palis (son exemple « 33 palettes, 66 palis » le suggère
 * à 2, il ne l'a pas énoncé comme règle), ni le poids d'un support. Ces trois valeurs, comme le coût
 * carburant au kilomètre (`fuelCostPerKmCents`), restent donc requises et sans défaut : une
 * simulation lancée sans ces quatre valeurs conclut `input_incomplete`.
 */
export const LOGISTICS_REQUIRED_PARAMETERS = [
  'palisPerPallet',
  'palletWeightKg',
  'paliWeightKg',
  'fuelCostPerKmCents',
] as const

export type LogisticsRequiredParameter = (typeof LOGISTICS_REQUIRED_PARAMETERS)[number]

/** Coût carburant au kilomètre, par type de véhicule (centimes). */
export type LogisticsFuelCostPerKmCents = Partial<Record<LogisticsVehicleType, number>>

/** Paramètres tels qu'ils sortent de la résolution : défauts garantis, requis encore optionnels. */
export interface LogisticsParameters {
  deliveryDeadline: string
  stopDurationSeconds: number
  hubTransferDurationSeconds: number
  maxContinuousDrivingMinutes: number
  mandatoryBreakMinutes: number
  detourFactor: number
  /** Emplacements de pali occupés par une palette : l'unité commune du calcul de capacité. */
  palisPerPallet?: number
  palletWeightKg?: number
  paliWeightKg?: number
  fuelCostPerKmCents?: LogisticsFuelCostPerKmCents
}

/** Surcharges stockées sur `logistics_simulations.parameters` : tout est optionnel. */
export type LogisticsParametersOverrides = Partial<LogisticsParameters>
